import { SharedAngular } from '@Angular.Runner/@types/sharedAngular';
import { toCamelCaseDeep } from '@Shared.Angular/flowingly.services/case-converter';

/**
 * @ngdoc directive
 * @name flowTableViewer
 * @module flowingly.components
 * @description  This comppnent is responsbile for displaying a table in the Runner history view.
 * ### Notes
 * This component shows the data entered by the user for the table, when they completed the step.
 * ### Properties
 * #### Inputs
 * * tableData : the JSON table data to display.
 * * noRowData : boolean tell if has any table data
 * * noRowDataMessage : the message to display when no table data
 * @usage
 * ```
     <flow-table-viewer table-data="item.value"></flow-table-viewer>
 * ```
 */

angular.module('flowingly.components').component('flowTableViewer', {
  bindings: {
    //this input contains the data saved when the user completed the form (which the table was part of)
    tableData: '<',
    tableSchema: '<',
    noRowData: '<',
    step: '<',
    noRowDataMessage: '@?'
  },
  templateUrl: 'table.viewer.component.tmpl.html',
  controller: [
    '$sce',
    'fileService',
    'lodashService',
    'sessionService',
    'flowinglyFormulaService',
    'flowinglyConstants',
    'tokenService',
    function (
      $sce,
      fileService: SharedAngular.FileService,
      lodashService,
      sessionService,
      flowinglyFormulaService,
      flowinglyConstants,
      tokenService
    ) {
      const $ctrl = this;
      $ctrl.$sce = $sce;
      $ctrl.table = {};
      let tableSchema;

      this.$onInit = function () {
        if (!$ctrl.noRowData) {
          if ($ctrl.tableSchema) {
            tableSchema = toCamelCaseDeep(JSON.parse($ctrl.tableSchema));
          }
          $ctrl.userId = sessionService.getUser().id;
          $ctrl.businessId = tokenService.getTenant().id;
          $ctrl.table = generateTable($ctrl.tableData);
        }

        $ctrl.formatFormula = (cells, cell) => {
          return flowinglyFormulaService.formatCell(
            $ctrl.step.schema,
            cells,
            cell
          );
        };

        $ctrl.isCurrencyCell = (cells, cell) => {
          const cellDetails = tableSchema.find(
            (option) => option.id == cell.id && option.type == cell.type
          );
          return flowinglyFormulaService.isCurrencyCell(
            $ctrl.step.schema,
            cells,
            cellDetails
          );
        };

        $ctrl.isNumberCell = (cells, cell) => {
          const cellDetails = tableSchema.find(
            (option) => option.id == cell.id && option.type == cell.type
          );
          return flowinglyFormulaService.isNumberCell(
            $ctrl.step.schema,
            cells,
            cellDetails
          );
        };
      };

      /// PRIVATE METHODS //////////////////////////////////////////////////////////

      function generateTable(tableData) {
        const table = JSON.parse(tableData);
        formatTypes(table);
        formatTotals(table);
        formatImageUrls(table);
        return table;
      }

      function formatImageUrls(table) {
        lodashService.forEach(table.rows, function (row) {
          lodashService.forEach(row.cells, function (cell) {
            if (cell.type === flowinglyConstants.tableCellType.FILE) {
              //if no file was uploaded cell.value will be undefined
              const downloadLink = cell.value
                ? fileService.getDownloadLink(cell.value)
                : undefined;
              cell.value = downloadLink
                ? {
                    fileId: cell.value,
                    downloadLink: downloadLink
                  }
                : undefined;
            }
          });
        });
      }

      function formatTypes(table) {
        lodashService.forEach(table.rows, function (row) {
          lodashService.forEach(row.cells, function (cell) {
            if (
              cell.type === flowinglyConstants.tableCellType.LOOKUP ||
              cell.type === flowinglyConstants.tableCellType.FORMULA
            ) {
              const cellSchema = tableSchema.find((s) => s.id === cell.id);
              if (cellSchema && cellSchema.lookupConfig) {
                cell.lookupConfig = cellSchema.lookupConfig;
              }
              if (cellSchema && cellSchema.formulaConfig) {
                cell.formulaConfig = cellSchema.formulaConfig;
              }
            }
          });
        });
      }

      function formatTotals(table) {
        //if there are any number / currency columns, we create an array of totals
        let foundNumberOrCurrency = false;
        $ctrl.sumCells = [];
        lodashService.forEach(table.rows, function (row) {
          lodashService.forEach(row.cells, function (cell) {
            const existing = lodashService.find(
              $ctrl.sumCells,
              function (sumCell) {
                return sumCell.id === cell.id;
              }
            );

            if (
              flowinglyFormulaService.hasSumCell(
                $ctrl.step.schema,
                row.cells,
                cell
              )
            ) {
              foundNumberOrCurrency = true;
              if (existing) {
                existing.sum += calculateCellSum(cell);
              } else {
                const sumInitValue = calculateCellSum(cell);
                $ctrl.sumCells.push(createCellSum(cell, sumInitValue));
              }
            } else {
              if (!existing) {
                $ctrl.sumCells.push(createCellSum(cell));
              }
            }
          });
        });
        //if did not find any number or currency columns clear out the data so total row not displayed.
        if (!foundNumberOrCurrency) {
          $ctrl.sumCells = [];
        }
      }

      function createCellSum(cell, sum = null) {
        const sumCell = {
          id: cell.id,
          type: cell.type
        };
        if (cell.lookupConfig) {
          sumCell['lookupConfig'] = cell.lookupConfig;
        }
        if (cell.formulaConfig) {
          sumCell['formulaConfig'] = cell.formulaConfig;
        }
        if (sum !== null) {
          sumCell['sum'] = sum;
        }
        return sumCell;
      }

      function calculateCellSum(cell) {
        let sum = 0;
        const cellValue = cell.value === undefined ? 0.0 : cell.value;

        if (flowinglyFormulaService.hasLookupSumCell(cell)) {
          if (typeof cellValue === 'string' && cellValue.indexOf(',') >= 0) {
            const valueArray = cellValue.split(',');

            for (const v of valueArray) {
              if (!isNaN(parseFloat(v))) {
                sum += parseFloat(v);
              }
            }
          } else {
            if (!isNaN(parseFloat(cellValue))) {
              sum += parseFloat(cellValue);
            }
          }
        } else {
          sum += Number(cellValue);
        }

        return sum;
      }
    }
  ]
});
